import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { Vitals, Patient } from '../types';

interface WhoGrowthChartProps {
  patient: Patient;
  vitals: Vitals[];
}

const whoBoysData = [
  { month: 0, p3: 2.5, p15: 2.9, p50: 3.3, p85: 3.9, p97: 4.4 },
  { month: 6, p3: 6.4, p15: 7.1, p50: 7.9, p85: 8.9, p97: 9.8 },
  { month: 12, p3: 7.7, p15: 8.6, p50: 9.6, p85: 10.8, p97: 12.0 },
  { month: 18, p3: 8.8, p15: 9.8, p50: 10.9, p85: 12.2, p97: 13.7 },
  { month: 24, p3: 9.7, p15: 10.8, p50: 12.2, p85: 13.7, p97: 15.3 },
  { month: 30, p3: 10.5, p15: 11.8, p50: 13.3, p85: 15.0, p97: 16.9 },
  { month: 36, p3: 11.3, p15: 12.7, p50: 14.3, p85: 16.2, p97: 18.3 },
  { month: 42, p3: 12.0, p15: 13.6, p50: 15.3, p85: 17.4, p97: 19.7 },
  { month: 48, p3: 12.7, p15: 14.4, p50: 16.3, p85: 18.6, p97: 21.2 },
  { month: 54, p3: 13.4, p15: 15.3, p50: 17.3, p85: 19.8, p97: 22.7 },
  { month: 60, p3: 14.1, p15: 16.2, p50: 18.3, p85: 21.0, p97: 24.2 },
];

const whoGirlsData = [
  { month: 0, p3: 2.4, p15: 2.8, p50: 3.2, p85: 3.7, p97: 4.2 },
  { month: 6, p3: 5.8, p15: 6.5, p50: 7.3, p85: 8.2, p97: 9.3 },
  { month: 12, p3: 7.0, p15: 7.9, p50: 8.9, p85: 10.2, p97: 11.5 },
  { month: 18, p3: 8.1, p15: 9.1, p50: 10.2, p85: 11.6, p97: 13.2 },
  { month: 24, p3: 9.0, p15: 10.2, p50: 11.5, p85: 13.1, p97: 14.8 },
  { month: 30, p3: 10.0, p15: 11.2, p50: 12.7, p85: 14.6, p97: 16.5 },
  { month: 36, p3: 10.8, p15: 12.2, p50: 13.9, p85: 16.0, p97: 18.1 },
  { month: 42, p3: 11.6, p15: 13.2, p50: 15.0, p85: 17.4, p97: 19.8 },
  { month: 48, p3: 12.3, p15: 14.2, p50: 16.1, p85: 18.8, p97: 21.5 },
  { month: 54, p3: 13.0, p15: 15.1, p50: 17.2, p85: 20.2, p97: 23.2 },
  { month: 60, p3: 13.7, p15: 16.0, p50: 18.2, p85: 21.6, p97: 24.9 },
];

const WhoGrowthChart: React.FC<WhoGrowthChartProps> = ({ patient, vitals }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!vitals || vitals.length === 0 || !chartRef.current || !patient.dob) return;

    d3.select(chartRef.current).selectAll('*').remove();

    const margin = { top: 20, right: 40, bottom: 40, left: 40 };
    const width = chartRef.current.clientWidth - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const isGirl = patient.gender?.toUpperCase() === 'FEMALE';
    const refData = isGirl ? whoGirlsData : whoBoysData;

    // Use total 60 months for X
    const x = d3.scaleLinear().domain([0, 60]).range([0, width]);
    const y = d3.scaleLinear().domain([0, 25]).range([height, 0]);

    // Grid lines for X and Y
    const xGrid = d3.axisBottom(x)
      .tickSize(-height)
      .tickFormat(() => "")
      .ticks(10);
      
    const yGrid = d3.axisLeft(y)
      .tickSize(-width)
      .tickFormat(() => "")
      .ticks(5);

    svg.append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(0,${height})`)
      .call(xGrid)
      .selectAll('line')
      .style('stroke', '#f1f5f9');
      
    svg.append('g')
      .attr('class', 'grid')
      .call(yGrid)
      .selectAll('line')
      .style('stroke', '#f1f5f9');

    // Axes
    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(10))
      .attr('color', '#64748b');

    svg.append('g')
      .call(d3.axisLeft(y).ticks(5))
      .attr('color', '#64748b');
      
    // Labels
    svg.append('text')
      .attr('x', width / 2)
      .attr('y', height + 35)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('fill', '#475569')
      .attr('font-weight', '500')
      .text('Age (Months)');

    svg.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', -30)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('fill', '#475569')
      .attr('font-weight', '500')
      .text('Weight (kg)');

    // Draw reference lines
    const createLine = (key: 'p3' | 'p15' | 'p50' | 'p85' | 'p97') => d3.line<any>()
      .x(d => x(d.month))
      .y(d => y(d[key]))
      .curve(d3.curveMonotoneX);

    const refColors = { p3: '#ef4444', p15: '#f59e0b', p50: '#10b981', p85: '#f59e0b', p97: '#ef4444' };

    ['p3', 'p15', 'p50', 'p85', 'p97'].forEach((p) => {
      svg.append('path')
        .datum(refData)
        .attr('fill', 'none')
        .attr('stroke', refColors[p as keyof typeof refColors])
        .attr('stroke-width', p === 'p50' ? 2 : 1)
        .attr('stroke-dasharray', p !== 'p50' ? '4,4' : 'none')
        .attr('d', createLine(p as any));
        
      // Add text label for percentile
      svg.append('text')
        .attr('x', x(60) + 5)
        .attr('y', y(refData[refData.length - 1][p as keyof typeof refData[0]]))
        .attr('fill', refColors[p as keyof typeof refColors])
        .attr('font-size', '10px')
        .attr('alignment-baseline', 'middle')
        .text(p.toUpperCase());
    });

    // Plot patient data
    const dob = new Date(patient.dob);
    
    const patientData = vitals.map(v => {
      const recDate = new Date(v.recordedAt);
      const diffTime = Math.abs(recDate.getTime() - dob.getTime());
      const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30.4375);
      return { month: diffMonths, weight: v.weight };
    }).filter(d => d.month <= 60);

    if (patientData.length > 0) {
      const patientLine = d3.line<{month: number, weight: number}>()
        .x(d => x(d.month))
        .y(d => y(d.weight))
        .curve(d3.curveMonotoneX);

      svg.append('path')
        .datum(patientData)
        .attr('fill', 'none')
        .attr('stroke', '#6366f1') // Indigo 500
        .attr('stroke-width', 3)
        .attr('d', patientLine);

      svg.selectAll('.patient-dot')
        .data(patientData)
        .enter().append('circle')
        .attr('class', 'patient-dot')
        .attr('cx', d => x(d.month))
        .attr('cy', d => y(d.weight))
        .attr('r', 5)
        .attr('fill', '#4f46e5') // Indigo 600
        .attr('stroke', '#fff')
        .attr('stroke-width', 2);
    }

  }, [patient, vitals]);

  return (
    <div className="bg-white border text-sm border-slate-200 rounded-lg p-4 w-full">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h4 className="font-bold text-slate-700 text-sm">WHO Growth Chart (0-5 Years)</h4>
          <p className="text-xs text-slate-500">Weight-for-Age Percentiles ({patient.gender?.toUpperCase() === 'FEMALE' ? 'Girls' : 'Boys'})</p>
        </div>
        <div className="flex items-center gap-3 text-xs font-semibold bg-slate-50 px-3 py-1.5 rounded-md border border-slate-100">
          <span className="text-emerald-500 flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> Median (P50)</span>
          <span className="text-red-500 flex items-center gap-1"><div className="w-2 h-0.5 border-t-2 border-dashed border-red-500"></div> P3 / P97</span>
          <span className="text-indigo-500 flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-indigo-500"></div> Patient Weight</span>
        </div>
      </div>
      <div ref={chartRef} className="w-full h-[300px]"></div>
    </div>
  );
};

export default WhoGrowthChart;
